import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { FILING_LABEL } from "@/lib/constants";

export const PriceDialog = ({ quote, onDone }) => {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(quote.monthly_premium || Math.round((quote.estimate_low + quote.estimate_high) / 2));
  const [note, setNote] = useState(quote.admin_note || "");
  const save = async (decline = false) => {
    try {
      await api.patch(`/admin/quotes/${quote.id}`, { monthly_premium: decline ? null : Number(price), admin_note: note, decline });
      toast.success(decline ? "Quote declined" : "Final price sent to customer");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-set-price-btn-${quote.id}`}>{quote.status === "priced" ? "Edit price" : "Set price"}</Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-price-dialog">
        <DialogHeader><DialogTitle>Final price · {quote.full_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Estimate shown to customer: ${quote.estimate_low}–${quote.estimate_high}/mo</p>
        <div className="space-y-1.5">
          <Label htmlFor="price">Monthly premium (USD)</Label>
          <Input id="price" type="number" min="1" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="admin-price-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="note">Note to customer (optional)</Label>
          <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-price-note-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => save(true)} data-testid="admin-decline-quote-btn">Decline</Button>
          <Button className="bg-blue-700 text-white hover:bg-blue-800 hover:text-white" disabled={!(Number(price) > 0)} onClick={() => save(false)} data-testid="admin-save-price-btn">Send price</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FilingDialog = ({ policy, onDone }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(policy.filing_status);
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      await api.patch(`/admin/policies/${policy.id}/filing`, { filing_status: status, note });
      toast.success("Filing status updated");
      setOpen(false);
      setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-update-filing-status-btn-${policy.id}`}>Update filing</Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-filing-dialog">
        <DialogHeader><DialogTitle>Filing · {policy.policy_number}</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Filing status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-filing-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(FILING_LABEL).filter(([k]) => (policy.sr22_required === false ? ["not_required", "cancelled"].includes(k) : k !== "not_required")).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`filing-option-${k}`}>{policy.sr22_required === false && k === "cancelled" ? "Cancelled" : v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fnote">Note (shown to customer)</Label>
          <Textarea id="fnote" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Filed electronically with state DMV, confirmation #…" data-testid="admin-filing-note-input" />
        </div>
        <DialogFooter>
          <Button className="bg-blue-700 text-white hover:bg-blue-800 hover:text-white" onClick={save} data-testid="admin-update-filing-status-btn">Save status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
